import { request } from '@umijs/max';
import type {
  ProjectSettingsResponse,
  UpdateProjectSettingsRequest,
} from '@/types/projectSettings';
import { assertSuccess } from '@/utils/apiHelpers';
import { createLogger } from '@/utils/logger';
import type {
  CreatedProject,
  CreateProjectRequest,
  InvitationLinkResponse,
  InviteProjectAdminRequest,
  OrgProjectsDashboardResponse,
  ProjectDetail,
  RegisteredProjectAdminResponse,
  UserOrganization,
} from './types';

const logger = createLogger('OrgAdminService');

// ============================================================================
// 【中心管理】Center Management
// ============================================================================

/**
 * 获取当前用户参与的组织列表（包含角色信息）
 */
export async function getMyCenters(): Promise<{
  success: boolean;
  data: UserOrganization[];
}> {
  const response = await request<{
    status: string;
    data: UserOrganization[];
    message: string;
    code: string;
    timestamp: string;
  }>('/v1/users/me/organizations', {
    method: 'GET',
  });

  assertSuccess(response, '获取组织列表失败');

  logger.info('获取组织列表成功', { count: response.data?.length || 0 });
  return {
    success: true,
    data: response.data || [],
  };
}

/**
 * 获取组织项目列表面板数据（新版聚合接口）
 * 一次调用即可获取：
 * 1. 组织级统计信息（项目总数、项目总人数）
 * 2. 项目列表（包含每个项目的详细统计）
 * 3. 分页信息
 *
 * @param organizationId - 组织ID
 * @param options - 可选参数
 * @returns 组织项目面板数据
 */
export async function getOrgProjectsDashboard(
  organizationId: string,
  options?: {
    skip?: number;
    limit?: number;
    status?: 'active' | 'inactive' | 'deprecated';
    require_fresh?: boolean;
  },
): Promise<{
  success: boolean;
  data: OrgProjectsDashboardResponse;
}> {
  const params: Record<string, any> = {
    skip: options?.skip ?? 0,
    limit: options?.limit ?? 100, // 默认获取所有项目
  };

  if (options?.status) {
    params.status = options.status;
  }
  if (options?.require_fresh !== undefined) {
    params.require_fresh = options.require_fresh;
  }

  const response = await request<{
    status: string;
    data: OrgProjectsDashboardResponse;
    message: string;
    code?: string;
    timestamp: string;
    trace_id?: string;
  }>(`/v1/workspace/dashboard/org/${organizationId}/projects`, {
    method: 'GET',
    params,
  });

  assertSuccess(response, '获取组织项目面板数据失败');

  logger.info('获取组织项目面板数据成功', {
    organizationId,
    projectCount: response.data?.stats?.total_project_count || 0,
    totalMembers: response.data?.stats?.total_member_count || 0,
  });

  return {
    success: true,
    data: response.data,
  };
}

/**
 * 获取组织已注册项目管理员列表面板数据（新版聚合接口）
 * 后端已完成：
 * 1. 按用户聚合（同一用户管理多个项目会合并为一条记录）
 * 2. 授权项目列表整合
 * 3. 最早加入时间计算
 * 4. 手机号自动脱敏
 *
 * @param organizationId - 组织ID
 * @param options - 可选参数
 * @returns 已注册项目管理员列表
 */
export async function getOrgRegisteredAdmins(
  organizationId: string,
  options?: {
    skip?: number;
    limit?: number;
    require_fresh?: boolean;
  },
): Promise<{
  success: boolean;
  data: RegisteredProjectAdminResponse;
}> {
  const params: Record<string, any> = {
    skip: options?.skip ?? 0,
    limit: options?.limit ?? 100, // 默认获取所有管理员
  };

  if (options?.require_fresh !== undefined) {
    params.require_fresh = options.require_fresh;
  }

  const response = await request<{
    status: string;
    data: RegisteredProjectAdminResponse;
    message: string;
    code?: string;
    timestamp: string;
    trace_id?: string;
  }>(`/v1/workspace/dashboard/org/${organizationId}/admins/registered`, {
    method: 'GET',
    params,
  });

  assertSuccess(response, '获取已注册项目管理员列表失败');

  logger.info('获取已注册项目管理员列表成功', {
    organizationId,
    adminCount: response.data?.total || 0,
  });

  return {
    success: true,
    data: response.data,
  };
}

// ============================================================================
// 【项目管理】Project Management
// ============================================================================

/**
 * 获取组织项目列表（用于其他组件独立调用，如项目详情页下拉菜单）
 * 注意：主工作台页面请使用 getOrgProjectsDashboard 替代
 */
export async function getCenterProjects(
  organizationId: string,
  options?: {
    skip?: number;
    limit?: number;
    status?: 'active' | 'inactive' | 'deprecated';
    require_fresh?: boolean;
  },
): Promise<{
  success: boolean;
  data: any[];
}> {
  const params: Record<string, any> = {};
  if (options?.skip !== undefined) {
    params.skip = options.skip;
  }
  if (options?.limit !== undefined) {
    params.limit = options.limit;
  }
  if (options?.status) {
    params.status = options.status;
  }
  if (options?.require_fresh !== undefined) {
    params.require_fresh = options.require_fresh;
  }

  const response = await request<{
    status: string;
    data: {
      items: ProjectDetail[];
      pagination: {
        total_items: number;
        total_pages: number;
        page: number;
        page_size: number;
      };
    };
    message: string;
    code: string;
    timestamp: string;
    trace_id?: string;
  }>(`/v1/orgs/${organizationId}/projects`, {
    method: 'GET',
    params,
  });

  assertSuccess(response, '获取项目列表失败');

  logger.info('获取项目列表成功', {
    organizationId,
    count: response.data?.items?.length || 0,
  });

  // 简单转换格式
  const projects = (response.data?.items || []).map((project) => ({
    project_id: project.id,
    project_name: project.name,
    project_type: project.project_type,
    project_status: project.status,
    organization_id: project.organization_id,
    organization_name: '',
    roles: [],
    primary_role: '',
    staff_title: '',
    assigned_at: project.created_at,
    created_at: project.created_at,
    updated_at: project.updated_at,
  }));

  return {
    success: true,
    data: projects,
  };
}

// ============================================================================
// 【管理员管理】Admin Management
// ============================================================================

/**
 * 获取项目管理员列表（用于其他组件独立调用，如邀请模态框）
 * 注意：主工作台页面请使用 getOrgRegisteredAdmins 替代
 */
export async function getProjectAdmins(projectId: number): Promise<{
  success: boolean;
  data: any[];
}> {
  const response = await request<{
    status: string;
    data: any[];
    message: string;
    code: string;
    timestamp: string;
  }>(`/v1/projects/${projectId}/admins`, {
    method: 'GET',
  });

  assertSuccess(response, '获取项目管理员列表失败');

  const data = response.data || [];
  logger.info('获取项目管理员列表成功', { projectId, count: data.length });

  return {
    success: true,
    data,
  };
}

/**
 * 为组织创建项目
 * project_type 始终为 general；settings、management_cycle_months、start_date 来自创建弹窗
 */
export async function createProject(
  organizationId: string,
  params: CreateProjectRequest,
): Promise<{
  success: boolean;
  data: CreatedProject;
}> {
  const requestBody = {
    name: params.name,
    project_type: 'general' as const,
    description: params.description ?? params.name,
    management_cycle_months: params.management_cycle_months ?? 24,
    inclusion_criteria: params.inclusion_criteria,
    exclusion_criteria: params.exclusion_criteria,
    ...(params.start_date != null ? { start_date: params.start_date } : {}),
    ...(params.settings != null ? { settings: params.settings } : {}),
  };

  const response = await request<{
    status: string;
    data: CreatedProject;
    message: string;
    code: string;
    timestamp: string;
    trace_id?: string;
  }>(`/v1/orgs/${organizationId}/projects`, {
    method: 'POST',
    data: requestBody,
  });

  assertSuccess(response, '创建项目失败');

  logger.info('创建项目成功', { projectId: response.data?.id });
  return {
    success: true,
    data: response.data,
  };
}

/**
 * 获取项目详情
 */
export async function getProject(projectId: number): Promise<{
  success: boolean;
  data: ProjectDetail;
}> {
  const response = await request<{
    status: string;
    data: ProjectDetail;
    message: string;
    code: string;
    timestamp: string;
  }>(`/v1/projects/${projectId}`, {
    method: 'GET',
  });

  assertSuccess(response, '获取项目详情失败');

  logger.info('获取项目详情成功', { projectId });
  return {
    success: true,
    data: response.data,
  };
}

// ============================================================================
// 【项目设置】Project Settings（GET/PUT）
// ============================================================================

/**
 * 获取项目设置
 * GET /v1/projects/{project_id}/settings
 * 组织管理员用于查看/编辑，患者相关用于展示或业务逻辑
 */
export async function getProjectSettings(
  projectId: number,
  options?: { require_fresh?: boolean },
): Promise<{ success: boolean; data: ProjectSettingsResponse }> {
  const params: Record<string, unknown> = {};
  if (options?.require_fresh !== undefined) {
    params.require_fresh = options.require_fresh;
  }
  const response = await request<{
    status: string;
    data: ProjectSettingsResponse;
    message?: string;
    code?: string;
    timestamp?: string;
    trace_id?: string;
  }>(`/v1/projects/${projectId}/settings`, {
    method: 'GET',
    params: Object.keys(params).length ? params : undefined,
  });

  assertSuccess(response, '获取项目设置失败');
  logger.info('获取项目设置成功', { projectId });
  return {
    success: true,
    data: response.data,
  };
}

/**
 * 更新项目设置
 * PUT /v1/projects/{project_id}/settings
 * 全量更新：请求体即最终配置，整体覆盖，不做合并。management、modules 必填，modules 须包含全部子模块。
 */
export async function updateProjectSettings(
  projectId: number,
  body: UpdateProjectSettingsRequest,
  options?: { require_fresh?: boolean },
): Promise<{ success: boolean; data: ProjectSettingsResponse }> {
  const params: Record<string, unknown> = {};
  if (options?.require_fresh !== undefined) {
    params.require_fresh = options.require_fresh;
  }
  const response = await request<{
    status: string;
    data: ProjectSettingsResponse;
    message?: string;
    code?: string;
    timestamp?: string;
    trace_id?: string;
  }>(`/v1/projects/${projectId}/settings`, {
    method: 'PUT',
    data: body,
    params: Object.keys(params).length ? params : undefined,
  });

  assertSuccess(response, '更新项目设置失败');
  logger.info('更新项目设置成功', { projectId });
  return {
    success: true,
    data: response.data,
  };
}

// ============================================================================
// 【邀请管理】Invitation Management
// ============================================================================

/**
 * 生成项目管理员邀请链接
 */
export async function generateProjectAdminInvitation(
  centerId: string,
  params: InviteProjectAdminRequest,
): Promise<{
  success: boolean;
  data: InvitationLinkResponse;
}> {
  try {
    // 根据 role_id 验证必需的上下文字段
    // role_id=4: 项目管理员需要 project_id 和 organization_id
    if (params.role_id === 4) {
      if (!params.project_id) {
        throw new Error('项目管理员邀请需要指定 project_id');
      }
      if (!centerId) {
        throw new Error('项目管理员邀请需要指定 organization_id');
      }
    }

    // 将 project_id 从 string 转换为 number
    const project_id = params.project_id
      ? Number(params.project_id)
      : undefined;
    if (project_id !== undefined && Number.isNaN(project_id)) {
      throw new Error('project_id 必须是有效的数字');
    }

    // 构建最终请求数据
    // 请求体格式：{"phone_number": "xxxxxxxxxxx", "full_name": "xxx", "role_id": 4, "organization_id": "uuid", "project_id": 123, "expires_in_days": 7}
    // 其中 project_id 为邀请进入锁定的项目的 id，organization_id 为邀请进入的组织的 id
    const requestData = {
      phone_number: params.phone_number,
      full_name: params.full_name,
      role_id: params.role_id,
      organization_id: centerId,
      project_id,
      expires_in_days: params.expires_in_days,
    };

    const response = await request<{
      status: string;
      data: {
        id: string;
        token: string;
        phone_number: string;
        full_name: string;
        role_id: number;
        organization_id?: string;
        project_id?: number;
        group_id?: string;
        status: string;
        created_at: string;
        expires_at: string;
      };
      message: string;
      code?: string;
      timestamp: string;
    }>(`/v1/invitations`, {
      method: 'POST',
      data: requestData,
    });

    assertSuccess(response, '创建邀请失败');

    logger.info('创建邀请成功', { project_id });

    // 构建邀请链接
    const baseUrl = window.location.origin;
    const invitationLink = `${baseUrl}/invitation?token=${response.data.token}`;

    return {
      success: true,
      data: {
        ...response.data,
        invitation_link: invitationLink,
      },
    };
  } catch (error: any) {
    // 处理错误，确保抛出有意义的错误信息
    if (error.response?.status === 404) {
      throw new Error('创建邀请接口不存在，请检查后端API路径');
    }

    // 处理 409 CONFLICT 错误（已有待处理的邀请）
    // 检查 HTTP 状态码或响应中的 code 字段
    if (
      error.response?.status === 409 ||
      error.response?.data?.code === 'CONFLICT'
    ) {
      const responseData = error.response?.data;
      const conflictError = new Error(
        responseData?.message || '该手机号已有待处理的邀请',
      ) as Error & { code?: string };
      conflictError.code = 'CONFLICT';
      throw conflictError;
    }

    // 如果是验证错误，提取详细错误信息
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === 'string') {
        throw new Error(detail);
      } else if (Array.isArray(detail) && detail.length > 0) {
        throw new Error(detail[0]?.msg || detail);
      }
    }
    // 如果错误消息已经存在，直接抛出
    if (error.message) {
      throw error;
    }
    throw new Error('创建邀请失败，请检查输入参数');
  }
}
